import { settingsNavPathV3 } from "@circle-react/helpers/urlHelpers/settingsNavPathV3";
import { useDevelopersAccessFlags } from "@circle-react/hooks/developers/useDevelopersAccessFlags";
import { adminDashboardRoutes } from "@circle-react/lib/routes/adminDashboard";
import { coursesRoutes } from "@circle-react/lib/routes/courses";
import { siteRoutes } from "@circle-react/lib/routes/site";
import { workflowsRoutes } from "@circle-react/lib/routes/workflows";
import { useLocation } from "@tanstack/react-router";

import { t } from "@/i18n-js/instance";
import {
  memberSettingsPaths,
  planSettingsPath,
} from "@/react/helpers/urlHelpers";
import { useBrandedAppsFeatureFlags } from "@/react/hooks/useBrandedAppsFeatureFlags";

import { separator } from "./separator";
import type { DropdownButton, DropdownLink, DropdownOption } from "./types";
import { adminIconsV3 } from "./useAdminIcons";

interface AdminOptionsProps {
  content: DropdownLink[];
  hasAIAgentsAccess?: boolean;
  isAffiliatesEnabled: boolean;
  isBannerVisible: boolean;
  isCircleV4: boolean;
  isCoursesV2Enabled: boolean;
  isWorkflowsEnabled: boolean;
  memberCount?: number;
  shortcutsModalButton: DropdownButton | null;
  showCustomizeTheme: boolean;
  toggleTheme: DropdownButton[];
  viewAs: DropdownButton;
}

export const useAdminOptions = ({
  memberCount,
  showCustomizeTheme,
  toggleTheme,
  viewAs,
  content,
  isWorkflowsEnabled,
  isAffiliatesEnabled,
  isBannerVisible,
  shortcutsModalButton,
  hasAIAgentsAccess = true,
  isCoursesV2Enabled = false,
  isCircleV4 = false,
}: AdminOptionsProps): DropdownOption[] => {
  const {
    adminDashboardIcon,
    membersIcon,
    coursesIcon,
    eventsIcon,
    marketingHubIcon,
    workflowsIcon,
    paywallsIcon,
    plansIcon,
    analyticsIcon,
    affiliatesIcon,
    settingsIcon,
    customizeThemeIcon,
    inviteMemberIcon,
    plusIcon,
    aiAgentsIcon,
    aiInboxIcon,
  } = adminIconsV3;
  const { isOverviewTabEnabled } = useDevelopersAccessFlags();

  const { brandedAppsBuilder, brandedApp } = useBrandedAppsFeatureFlags();

  const pathname = useLocation({
    select: location => location.pathname,
  });
  const options: DropdownOption[] = [];

  options.push({
    value: t("sidebar_admin_dashboard"),
    label: t("sidebar_admin_dashboard"),
    pathTo: adminDashboardRoutes.index(),
    icon: adminDashboardIcon,
    iconSize: 20,
  });

  const membersOption: DropdownOption = {
    value: "members",
    label: t("section_audience"),
    pathTo: memberSettingsPaths.index(),
    icon: membersIcon,
    iconSize: 20,
  };

  if (memberCount) {
    membersOption.badge = String(memberCount);
  }

  options.push(membersOption);

  options.push(...content);

  if (isCircleV4) {
    options.push({
      value: t("sidebar_events"),
      label: t("sidebar_events"),
      pathTo: "/settings/events",
      icon: eventsIcon,
      iconSize: 20,
    });
  }

  if (isCoursesV2Enabled) {
    options.push({
      value: t("sidebar_courses"),
      label: t("sidebar_courses"),
      pathTo: coursesRoutes.overview(),
      icon: coursesIcon,
      iconSize: 20,
    });
  }

  options.push({
    value: t("sidebar_email"),
    label: t("sidebar_email"),
    pathTo: settingsNavPathV3.email.overview,
    icon: marketingHubIcon,
    iconSize: 20,
  });

  if (isWorkflowsEnabled) {
    options.push({
      value: t("sidebar_workflows"),
      label: t("sidebar_workflows"),
      pathTo: workflowsRoutes.list(),
      icon: workflowsIcon,
      iconSize: 20,
    });
  }

  options.push({
    value: t("analytics.analytics"),
    label: t("analytics.analytics"),
    pathTo: "/settings/analytics",
    icon: analyticsIcon,
    iconSize: 20,
  });

  options.push({
    value: t("sidebar_ai_agents"),
    label: t("sidebar_ai_agents"),
    pathTo: "/settings/ai-agents/agents",
    icon: aiAgentsIcon,
    iconSize: 20,
  });

  if (hasAIAgentsAccess) {
    options.push({
      value: t("sidebar_ai_inbox"),
      label: t("sidebar_ai_inbox"),
      pathTo: "/settings/ai-inbox",
      icon: aiInboxIcon,
      iconSize: 20,
    });
  }

  options.push({
    value: t("sidebar_paywalls"),
    label: t("sidebar_paywalls"),
    pathTo: "/settings/paywalls",
    icon: paywallsIcon,
    iconSize: 20,
  });

  if (isAffiliatesEnabled) {
    options.push({
      value: t("sidebar_paywalls_affiliates"),
      label: t("sidebar_paywalls_affiliates"),
      pathTo: "/settings/affiliates_settings",
      icon: affiliatesIcon,
      iconSize: 20,
    });
  }

  options.push({
    value: t("pricing_plans.title"),
    label: t("settings.billing.plans.title"),
    pathTo: planSettingsPath(),
    icon: plansIcon,
    iconSize: 20,
  });

  if (brandedApp.isEnabled) {
    options.push({
      value: t("settings_nav_v3.settings.branded_app"),
      label: t("settings_nav_v3.settings.branded_app"),
      pathTo: brandedAppsBuilder.isEnabled
        ? "/settings/plus"
        : "/settings/plus/lite",
      icon: plusIcon,
      iconSize: 20,
    });
  }

  options.push({
    label: t("settings_nav_v3.site.label"),
    value: t("settings_nav_v3.site.label"),
    icon: "20-display",
    pathTo: siteRoutes.index(),
    iconSize: 20,
  });

  options.push({
    label: t("settings_nav_v3.developers.title"),
    value: t("settings_nav_v3.developers.title"),
    icon: "20-code-tag",
    pathTo: isOverviewTabEnabled
      ? settingsNavPathV3.developers.overview
      : settingsNavPathV3.developers.api,
    iconSize: 20,
  });

  options.push({
    value: t("sidebar_settings"),
    label: t("sidebar_settings"),
    pathTo: "/settings",
    icon: settingsIcon,
    iconSize: 20,
  });

  options.push(separator);

  options.push({
    value: t("settings_nav_v3.site_builder.label"),
    label: t("settings_nav_v3.site_builder.label"),
    pathTo: settingsNavPathV3.content.pages,
    icon: "20-display",
    iconSize: 20,
  });

  if (showCustomizeTheme) {
    options.push({
      value: t("customize_theme.title"),
      label: t("customize_theme.title"),
      pathTo: "/settings/theme",
      icon: customizeThemeIcon,
      iconSize: 20,
    });
  }

  options.push(...toggleTheme);

  if (shortcutsModalButton) {
    options.push(shortcutsModalButton);
  }

  options.push(separator);

  if (!isBannerVisible) {
    options.push(viewAs);
  }

  options.push({
    value: t("sidebar_invite_member"),
    label: t("sidebar_invite_member"),
    pathTo: `${settingsNavPathV3.members.invite}?from=${pathname}`,
    icon: inviteMemberIcon,
    iconSize: 20,
  });

  return options;
};
